import { FeaturesReady } from '@growthbook/growthbook-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import {
  CARD_BADGE_HEIGHT,
  CARD_BADGE_WIDTH,
  STATUS
} from 'dpl/breeder_preferred_qualifications/utils/constants';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import FavoriteButtonV2 from 'dpl/favorites/components/FavoriteButtonV2';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import CardFooter from './CardFooter';
import CardMediaCarousel from './CardMediaCarousel';

const CARD_PHOTO_SIZE = isLessThanBreakpoint('lg') ? '180px' : '260px';

export default function Card({
  profile,
  lastUpdated,
  analytics,
  onFavoriteChange,
  trackInView
}) {
  const stateLander = useSelector(({ server }) => server.stateLander);
  const locationTitle = useSelector(({ server }) => server.locationTitle);
  const citySlug = useSelector(({ server }) => server.citySlug);

  const stateSlug = useSelector(
    ({ server }) => server.preappliedFilters?.stateSlug
  );

  const {
    cropped_photo_url: croppedPhotoUrl,
    delivery_methods: deliveryMethods,
    name,
    description,
    distance_in_miles: distance,
    availability_description: availabilityDescription,
    available_puppy_count: availablePuppyCount,
    display_apply_cta: displayApplyCta,
    profile_photo_url: profilePhotoUrl,
    snoozed,
    url,
    missing_photo_url: missingPhotoUrl,
    result_photos: resultPhotos,
    breeder_breed_id: breederBreedId,
    breed_id: breedId,
    id: breederProfileId,
    testimonials_count: testimonialsCount,
    preferred_qualification: preferredQualification
  } = profile;

  const isOutOfState = stateSlug !== profile.state_slug;

  const photosWithAlt = useMemo(
    () =>
      resultPhotos.map(photo => ({
        ...photo,
        alt: locationTitle ? `${name} in ${locationTitle}` : name
      })),
    [locationTitle, lastUpdated]
  );

  const displayPhotoUrl = croppedPhotoUrl || profilePhotoUrl;

  const smartImageProps = {
    Tag: 'div',
    className: 'bg-center cover br3',
    crop: true,
    loadingClass: 'bg-light-gray',
    height: CARD_PHOTO_SIZE,
    width: CARD_PHOTO_SIZE,
    allowContextMenu: true
  };

  function getLocationDescription() {
    const mileOrMiles = distance === 1 ? 'mile' : 'miles';
    const milesAwayLabel = distance
      ? `${distance} ${mileOrMiles} away from`
      : '';

    if (stateLander && isOutOfState) {
      return `${milesAwayLabel} ${locationTitle}`.trim();
    } else if (stateLander) {
      return profile.facility_location_full_state;
    } else if (citySlug && milesAwayLabel) {
      return `${milesAwayLabel} ${locationTitle}`;
    } else if (citySlug) {
      return locationTitle;
    }

    return profile.facility_location;
  }

  const currentUser = useCurrentUser();
  const { isBuyer, isLoggedIn } = currentUser;

  const favoriteAnalytics = {
    ...analytics,
    breed_id: breedId,
    breeder_name: name,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    page_number: analytics.page_number || null,
    position: analytics.position || null,
    puppies_available: availablePuppyCount,
    transportation: Object.keys(deliveryMethods || {}).reduce(
      (allTypes, key) => {
        return [...allTypes, ...deliveryMethods[key].map(dm => dm.name)];
      },
      []
    )
  };

  const shouldShowFavoriteButton = !isLoggedIn || isBuyer;

  const computedAnalytics = {
    ...analytics,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD
  };

  function handleClick() {
    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...computedAnalytics,
      breed_id: breedId,
      miles_away: distance,
      transportation: favoriteAnalytics.transportation
    });
  }

  const isPreferredBreeder =
    preferredQualification?.status === STATUS.PREFERRED;

  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: inView => {
      if (inView) trackInView();
    }
  });

  return (
    <a
      ref={ref}
      className="BreederSearchResultsCard h-100 db"
      href={url}
      onClick={handleClick}
    >
      <div className="BreederSearchResultsCard__container h-100 flex flex-column justify-between br12 ba b--default">
        <div>
          <div className="BreederSearchResultsCard__info flex ph4 pt4 mb2 justify-between gap-12 md:gap-16">
            <div className="flex items-center gap-12 md:gap-16 overflow-hidden">
              <div className="relative">
                <UserAvatar
                  user={{
                    id: profile.user_id,
                    initials: profile.initials,
                    profile_photo_url: displayPhotoUrl
                  }}
                />
                {isPreferredBreeder && (
                  <div className="absolute right-0 bottom-0 nb2">
                    <PreferredBreederBadge
                      height={CARD_BADGE_HEIGHT}
                      width={CARD_BADGE_WIDTH}
                    />
                  </div>
                )}
              </div>
              <div className="overflow-auto">
                <p className="BreederSearchResultsItem__name font-serif fw-medium font-18 md:font-20 truncate">
                  {name}
                </p>
                <p className="font-12 md:font-14 secondary truncate">
                  {getLocationDescription()}
                </p>
              </div>
            </div>
            {shouldShowFavoriteButton && (
              <div className="pv1 ph1">
                <FavoriteButtonV2
                  analytics={favoriteAnalytics}
                  followableTypeId={breederBreedId}
                  followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
                  checkedIconProps={{
                    height: '20px',
                    width: '20px',
                    name: 'fetch-favorite',
                    className: 'red'
                  }}
                  uncheckedIconProps={{
                    height: '20px',
                    width: '20px',
                    name: 'fetch-favorite',
                    className: 'default-color o-50'
                  }}
                  onChange={onFavoriteChange}
                />
              </div>
            )}
          </div>
          {!snoozed && (
            <div className="flex mt4 mt6-md overflow-hidden">
              <div className="BreederSearchResultsItem__image w-100 h-100 br3 relative z-0">
                <FeaturesReady timeout={500}>
                  <CardMediaCarousel
                    mediaFiles={
                      photosWithAlt.length === 0
                        ? [{ url: missingPhotoUrl }]
                        : photosWithAlt
                    }
                    smartImageProps={smartImageProps}
                    showCaptions
                    analytics={{
                      ...computedAnalytics,
                      breeder_preferred_status: undefined,
                      breeder_profile_id: undefined
                    }}
                  />
                </FeaturesReady>
              </div>
            </div>
          )}
          <div className="mh4 mt4 pb4 f2">
            <p className="secondary">{description}</p>
          </div>
        </div>
        {!snoozed && (
          <>
            <CardFooter
              snoozed={snoozed}
              deliveryMethods={deliveryMethods}
              breederProfileUrl={url}
              displayApplyCta={displayApplyCta}
              breederProfileId={breederProfileId}
              analytics={computedAnalytics}
              availabilityDescription={availabilityDescription}
              testimonialsCount={testimonialsCount}
            />
          </>
        )}
      </div>
    </a>
  );
}

Card.propTypes = {
  profile: PropTypes.shape({
    cropped_photo_url: PropTypes.string,
    availability_description: PropTypes.string,
    availability_info: PropTypes.string,
    display_apply_cta: PropTypes.bool,
    facility_location_full_state: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    delivery_methods: PropTypes.object,
    description: PropTypes.string,
    snoozed: PropTypes.bool,
    name: PropTypes.string,
    initials: PropTypes.string.isRequired,
    facility_location: PropTypes.string,
    distance_in_miles: PropTypes.number,
    profile_photo_url: PropTypes.string,
    result_photos: PropTypes.arrayOf(PropTypes.shape(PhotographPropTypes))
      .isRequired,
    url: PropTypes.string.isRequired,
    missing_photo_url: PropTypes.string,
    available_puppy_count: PropTypes.number,
    breeder_breed_id: PropTypes.number,
    id: PropTypes.number,
    user_id: PropTypes.number.isRequired,
    breed_id: PropTypes.number,
    testimonials_count: PropTypes.number,
    first_name: PropTypes.string,
    delivery_methods_count: PropTypes.number,
    preferred_qualification: PropTypes.shape({
      status: PropTypes.string
    }),
    state_slug: PropTypes.string
  }).isRequired,
  analytics: PropTypes.shape({
    page_number: PropTypes.number,
    position: PropTypes.number,
    view: PropTypes.string
  }).isRequired,
  lastUpdated: PropTypes.number,
  onFavoriteChange: PropTypes.func,
  trackInView: PropTypes.func
};

Card.defaultProps = {
  lastUpdated: null,
  onFavoriteChange: null,
  trackInView: () => {}
};
